import { apiFetch, apiGet, apiSend, SessionApiError } from '../../session/client';

/**
 * Everything the Store page reads and writes, and nothing it invents.
 *
 * Every call is an existing platform route — the ones the Android app's
 * storefront, product, basket, consultation and booking screens already use.
 * The basket is keyed on this browser's device handle, which `apiFetch`
 * sends; when somebody is signed in it sends their access token too. There is
 * no person id and no price in any body: the server decides whose basket this
 * is and what everything costs.
 */

// -- Shapes (mirrors of the platform's consumer views) -----------------------

export interface Price {
  readonly resolved: boolean;
  readonly currency?: string;
  readonly amountMinor?: number;
  readonly compareAtMinor?: number;
  readonly savingMinor?: number;
  readonly promotionNames?: readonly string[];
}

export interface Quantity {
  readonly amount: number;
  readonly unit: string;
}

export interface OfferCard {
  readonly id: string;
  readonly kind: 'PRODUCT' | 'SERVICE';
  readonly title: string;
  readonly summary?: string;
  readonly brandName?: string;
  readonly image?: { readonly url: string; readonly altText?: string };
  readonly storefrontId: string;
  readonly storefrontSlug: string;
  readonly storefrontName: string;
  readonly fulfilmentModes: readonly string[];
  readonly restriction: {
    readonly ageRestricted: boolean;
    readonly consultationRequired: boolean;
    readonly prescriptionRequired: boolean;
  };
  readonly price: Price;
  readonly bookability?: { readonly bookable: boolean; readonly nextAvailableAt?: string };
  readonly service?: { readonly serviceType: string; readonly typicalMinutes: number };
  readonly product?: {
    readonly presentation: string;
    readonly packSize?: Quantity;
    readonly netContent?: Quantity;
    readonly strength?: Quantity;
  };
  readonly stock?: { readonly known: boolean; readonly inStock?: boolean };
}

/** One offer's page. It names its shop as `storefront`, not with the card's three flat fields. */
export interface OfferDetail extends Omit<
  OfferCard,
  'image' | 'storefrontId' | 'storefrontSlug' | 'storefrontName'
> {
  readonly description?: string;
  readonly images: readonly { readonly url: string; readonly altText?: string }[];
  readonly categoryIds: readonly string[];
  readonly storefront: { readonly id: string; readonly slug: string; readonly name: string };
}

export interface OpeningHours {
  readonly weekday: number;
  readonly opens: string;
  readonly closes: string;
}

export interface StoreLocation {
  readonly id: string;
  readonly type: string;
  readonly town?: string;
  readonly postcode?: string;
  readonly coordinates?: { readonly latitude: number; readonly longitude: number };
}

export interface StoreCategory {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly offerCount: number;
}

export interface Store {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly shortDescription?: string;
  readonly about?: string;
  readonly specialisms: readonly string[];
  readonly fulfilmentModes: readonly string[];
  readonly image?: { readonly url: string };
  readonly rating?: number;
  readonly reviewCount?: number;
  readonly deliveryFromMinor?: number;
  readonly minimumOrderMinor?: number;
  readonly deliveryCurrency?: string;
  readonly registration?: {
    readonly regulator: string;
    readonly number: string;
    readonly superintendent?: string;
  };
  readonly openingHours?: readonly OpeningHours[];
  readonly deliveryEtaLabel?: string;
  readonly collectionEtaLabel?: string;
  readonly locations: readonly StoreLocation[];
  readonly promotions: readonly string[];
  readonly categories: readonly StoreCategory[];
  readonly publicPhone?: string;
}

export interface SearchPage {
  readonly results: readonly OfferCard[];
  readonly pageInfo: {
    readonly nextCursor?: string;
    readonly hasMore: boolean;
    readonly totalMatched?: number;
  };
}

export interface TaxonomyCategory {
  readonly id: string;
  readonly name: string;
  readonly imageUrl?: string;
}

export interface CartLine {
  readonly id: string;
  readonly listingId: string;
  readonly title: string;
  readonly quantity: number;
  readonly unitPriceMinor?: number;
  readonly lineTotalMinor?: number;
  readonly imageUrl?: string;
  readonly consultationId?: string;
}

export interface Cart {
  readonly id: string | null;
  readonly storefrontId?: string;
  readonly currency: string;
  readonly lines: readonly CartLine[];
  readonly itemCount: number;
  readonly subtotalMinor: number;
  readonly unpricedLineIds: readonly string[];
}

export type QuestionKind =
  | 'YES_NO'
  | 'SINGLE'
  | 'MULTI'
  | 'MEASUREMENT'
  | 'TEXT'
  | 'GP_PRACTICE'
  | 'NUMBER'
  | 'DATE'
  | 'ACKNOWLEDGEMENT'
  | 'DOCUMENT'
  | 'STOP';

export interface Question {
  readonly id: string;
  readonly kind: QuestionKind;
  readonly prompt: string;
  readonly help?: string;
  readonly required: boolean;
  readonly unit?: string;
  readonly min?: number;
  readonly max?: number;
  readonly integer?: boolean;
  readonly dateRange?: 'PAST' | 'FUTURE' | string;
  readonly maxFiles?: number;
  readonly section?: string;
  readonly showIf?: { readonly questionId: string; readonly anyOf: readonly string[] };
  readonly options?: readonly { readonly id: string; readonly label: string; readonly hint?: string }[];
}

export interface Requirement {
  readonly listingId: string;
  readonly itemName: string;
  readonly required: boolean;
  readonly available: boolean;
  readonly reason?: 'PHARMACY_MEDICINE' | 'PRESCRIPTION_MEDICINE' | 'PHARMACY_QUESTIONNAIRE';
  readonly questionnaire?: {
    readonly id: string;
    readonly title: string;
    readonly introduction?: string;
    readonly questions: readonly Question[];
  };
}

export type AnswerValue =
  | boolean
  | number
  | string
  | readonly string[]
  | { readonly value: number; readonly unit: string }
  | { readonly manual: boolean; readonly name: string }
  | { readonly documentIds: readonly string[] };

export interface Consultation {
  readonly id: string;
  readonly reference?: string;
  readonly status: string;
  readonly listingId: string;
}

export interface Slot {
  readonly startsAt: string;
  readonly endsAt: string;
  readonly durationMinutes: number;
  readonly remaining: number;
  readonly modes: readonly string[];
  readonly timeZone: string;
}

export interface Availability {
  readonly availabilityId: string;
  readonly listingId: string;
  readonly timeZone: string;
  readonly slots: readonly Slot[];
}

export interface Hold {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly mode: string;
  readonly expiresAt: string;
}

export interface Booking {
  readonly id: string;
  readonly listingId: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly timeZone: string;
  readonly mode: string;
  readonly status: string;
}

export interface Review {
  readonly id: string;
  readonly rating: number;
  readonly body: string;
  readonly reviewer: string;
  readonly subject: { readonly type: string; readonly label?: string };
  readonly response: { readonly body: string; readonly at: string } | null;
  readonly createdAt: string;
}

export interface ProviderCard {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly initials: string;
  readonly accent?: string;
  readonly image?: { readonly url: string };
  readonly rating?: number;
  readonly reviewCount?: number;
}

export interface Place {
  readonly postcode: string;
  readonly displayArea?: string;
  readonly coordinates?: { readonly latitude: number; readonly longitude: number };
}

export interface TestRow {
  readonly offer: OfferCard;
  readonly test: {
    readonly shelf: string;
    readonly collection: 'HOME' | 'CLINIC' | string;
    readonly clinicVisit?: boolean;
    readonly turnaround?: string;
  };
}

// -- Calls -------------------------------------------------------------------

const id = (value: string): string => encodeURIComponent(value);

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text.length > 0 ? `?${text}` : '';
}

/** The shop itself. `limit=1`: the page lists its shelves through search, not this list. */
export const getStore = (slug: string): Promise<Store> =>
  apiGet<Store>(`/consumer/storefronts/${id(slug)}?limit=1`);

/** One shop's shelf, through the same search every other page uses. */
export const searchStore = (input: {
  readonly storefrontId: string;
  readonly kind?: 'PRODUCT' | 'SERVICE';
  readonly q?: string;
  readonly categoryId?: string;
  readonly sort?: 'RELEVANCE' | 'PRICE_ASC' | 'PRICE_DESC';
  readonly limit?: number;
  readonly cursor?: string;
}): Promise<SearchPage> =>
  apiGet<SearchPage>(
    `/consumer/search${query({
      storefrontId: input.storefrontId,
      kind: input.kind,
      q: input.q,
      categoryId: input.categoryId,
      sort: input.sort,
      limit: input.limit ?? 48,
      cursor: input.cursor,
    })}`,
  );

export const getOffer = (listingId: string): Promise<OfferDetail> =>
  apiGet<OfferDetail>(`/consumer/offers/${id(listingId)}`);

/** The taxonomy, for the pictures on the shop's own shelves. */
export const getTaxonomy = (): Promise<{ readonly categories: readonly TaxonomyCategory[] }> =>
  apiGet<{ categories: TaxonomyCategory[] }>('/consumer/categories');

export const resolvePostcode = (postcode: string): Promise<Place> =>
  apiGet<Place>(`/consumer/location${query({ postcode })}`);

/** The providers the homepage composes for a postcode — the same cards, the same terms. */
export const getProviders = (postcode: string | undefined): Promise<readonly ProviderCard[]> =>
  apiGet<{ providers?: ProviderCard[] }>(`/consumer/homepage${query({ postcode })}`).then(
    (page) => page.providers ?? [],
  );

export const getReviews = (storefrontId: string, limit: number): Promise<readonly Review[]> =>
  apiGet<{ reviews: Review[] }>(
    `/consumer/reviews-public/storefronts/${id(storefrontId)}${query({ limit })}`,
  ).then((page) => page.reviews);

export const getTests = (): Promise<readonly TestRow[]> =>
  apiGet<{ tests: TestRow[] }>('/consumer/tests').then((hub) => hub.tests);

// -- The basket ----------------------------------------------------------------

export const getCart = (): Promise<Cart> => apiGet<Cart>('/consumer/cart');

/** One idempotency key per press, so a retried request is never a second add. */
export function newKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `web-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

export const addToCart = (
  listingId: string,
  quantity: number,
  key: string,
  consultationId?: string,
): Promise<Cart> =>
  apiSend<Cart>(
    'POST',
    '/consumer/cart/items',
    { listingId, quantity, ...(consultationId !== undefined ? { consultationId } : {}) },
    { headers: { 'idempotency-key': key } },
  );

/** Zero removes the line. */
export const setLineQuantity = (lineId: string, quantity: number, key: string): Promise<Cart> =>
  apiSend<Cart>(
    'POST',
    `/consumer/cart/items/${id(lineId)}`,
    { quantity },
    { headers: { 'idempotency-key': key } },
  );

export const clearCart = (key: string): Promise<Cart> =>
  apiSend<Cart>('POST', '/consumer/cart/clear', undefined, { headers: { 'idempotency-key': key } });

// -- Consultations (the P / POM gate) -------------------------------------------

export const getRequirement = (listingId: string): Promise<Requirement> =>
  apiGet<Requirement>(`/consumer/consultations/requirement/${id(listingId)}`);

/** Needs sign-in; the server binds the answers to this device and this listing. */
export const submitConsultation = (
  listingId: string,
  answers: readonly { readonly questionId: string; readonly value: AnswerValue }[],
): Promise<Consultation> => apiSend<Consultation>('POST', '/consumer/consultations', { listingId, answers });

/** A photo or document for a DOCUMENT question, stored in the patient's own Documents. */
export const uploadEvidence = (filename: string, dataBase64: string): Promise<{ id: string }> =>
  apiSend<{ id: string }>('POST', '/consumer/consultations/evidence', { filename, dataBase64 });

// -- Booking ------------------------------------------------------------------

export const getSlots = (listingId: string, from: Date, to: Date): Promise<readonly Availability[]> =>
  apiGet<{ availability: Availability[] }>(
    `/booking/slots${query({ listingId, from: from.toISOString(), to: to.toISOString() })}`,
  ).then((found) => found.availability);

export const holdSlot = (input: {
  readonly listingId: string;
  readonly availabilityId: string;
  readonly startsAt: string;
  readonly mode: string;
  readonly idempotencyKey: string;
}): Promise<Hold> => apiSend<Hold>('POST', '/me/bookings/holds', input);

export const confirmBooking = (holdId: string, idempotencyKey: string): Promise<Booking> =>
  apiSend<Booking>('POST', '/me/bookings', { holdId, idempotencyKey });

/** Lets go of a hold the customer walked away from, so the slot is free again. Best effort. */
export async function releaseHold(holdId: string): Promise<void> {
  await apiFetch(`/me/bookings/holds/${id(holdId)}/release`, { method: 'POST' }).catch(() => undefined);
}

export { SessionApiError };
